// Login.js — branded sign-in screen shown until the user authenticates.
//
// Two steps for everyone but the admin: the password, then a six-digit code from Google
// Authenticator (or any TOTP app). The first time, the second step shows the user their own QR
// code to scan; once one code from it has worked, it only ever asks for the code.
import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import './Login.css';

function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  // null = the password step; otherwise the backend's challenge: { mfa: 'enroll' | 'verify', qr, secret }
  const [challenge, setChallenge] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const backToPassword = () => {
    setChallenge(null); setCode(''); setPassword(''); setError(''); setShowSecret(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (challenge && code.replace(/\D/g, '').length !== 6) {
      setError('Enter the 6-digit code from the app.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await login(username, password, challenge ? code : '');
      if (res.ok) return;
      if (res.mfa) {
        setChallenge(res);
        setCode('');
        if (res.mfaError) setError(res.mfaError);
        return;
      }
      setChallenge(null);
      setError('Incorrect username or password.');
      setPassword('');
    } catch {
      setError('Could not sign in — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const enrolling = challenge && challenge.mfa === 'enroll';

  return (
    <div className="login-screen">
      {/* Ambient brand glow */}
      <div className="login-glow login-glow-1" />
      <div className="login-glow login-glow-2" />

      <form className={`login-card ${enrolling ? 'login-card-wide' : ''}`} onSubmit={handleSubmit} noValidate>
        <div className="login-brand">
          <img className="login-crest" src="/melbourne-city-crest.png" alt="Melbourne City crest" width="72" height="72" />
          <h1 className="login-title">Melbourne City</h1>
          <p className="login-subtitle">Insights</p>
        </div>

        {!challenge && (
          <>
            <div className="login-field">
              <label htmlFor="login-username">Username</label>
              <input
                id="login-username"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck="false"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setError(''); }}
                placeholder="Enter Username"
                autoFocus
              />
            </div>

            <div className="login-field">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                placeholder="Enter Password"
              />
            </div>
          </>
        )}

        {enrolling && (
          <div className="login-mfa-setup">
            <p className="login-mfa-title">Set up Google Authenticator</p>
            <ol className="login-mfa-steps">
              <li>Install <strong>Google Authenticator</strong> on your phone.</li>
              <li>Tap <strong>+</strong>, then <strong>Scan a QR code</strong>, and scan this one.</li>
              <li>Enter the 6-digit code it shows.</li>
            </ol>
            <img className="login-mfa-qr" src={challenge.qr} alt={`Authenticator QR code for ${username}`} />
            {showSecret ? (
              <div className="login-mfa-secret" title="Type this into the app instead of scanning">
                {challenge.secret.replace(/(.{4})/g, '$1 ').trim()}
              </div>
            ) : (
              <button type="button" className="login-link" onClick={() => setShowSecret(true)}>
                Can't scan it? Show the setup key
              </button>
            )}
          </div>
        )}

        {challenge && (
          <div className="login-field">
            <label htmlFor="login-code">
              {enrolling ? 'Code from the app' : `Authenticator code for ${username.trim().toLowerCase()}`}
            </label>
            <input
              id="login-code"
              className="login-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={code}
              onChange={(e) => { setCode(e.target.value.replace(/[^\d ]/g, '')); setError(''); }}
              placeholder="123 456"
              autoFocus
            />
          </div>
        )}

        {error && <div className="login-error" role="alert">{error}</div>}

        <button type="submit" className="login-submit" disabled={busy}>
          {busy ? (challenge ? 'Checking…' : 'Signing in…') : (challenge ? 'Verify' : 'Sign in')}
        </button>
        {challenge && (
          <button type="button" className="login-link login-back" onClick={backToPassword}>
            ← Use a different account
          </button>
        )}
      </form>
    </div>
  );
}

export default Login;
